/* Love Cashmere extensions. Keep Studio's product, cart and standard event components intact. */
(() => {
  'use strict';
  if (window.LoveCashmere) return;

  const productData = new Map();
  const accordionStates = new WeakMap();
  const pendingRoots = new Set();
  let scheduled = false;
  const normalise = (value) => String(value || '').trim().toLowerCase();
  const parse = (element) => {
    try { return JSON.parse(element?.textContent || '{}'); } catch { return {}; }
  };
  const findAll = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector),
  ];

  function inventoryState(data, variant) {
    if (!variant.managed) {
      if (data.made_to_order) return ['green', 'Made to Order - Delivery in 3-4 weeks'];
      return variant.on_sale ? ['orange', 'Low Stock'] : ['green', 'In Stock'];
    }
    if (variant.qty > 0) {
      if (variant.qty <= Number(data.threshold || 0)) {
        return ['red', data.show_qty ? `Low Stock: Only ${variant.qty} left!` : 'Low stock'];
      }
      return variant.on_sale ? ['orange', 'Low Stock'] : ['green', 'In Stock'];
    }
    if (variant.continue) return ['green', 'Pre-Order - Delivery in 3-4 weeks'];
    return ['grey', variant.coming_soon ? (variant.is_new ? 'New - Coming Soon' : 'Coming Soon') : 'Out of Stock'];
  }

  function updateInventory(root) {
    const data = parse(root.querySelector('[data-lc-inventory-map]'));
    const input = root.querySelector('product-form input.product-variant-id');
    const variant = data.variants?.[String(input?.value)];
    const inventory = root.querySelector('[data-inventory-display]');
    if (!variant || !inventory || inventory.classList.contains('hidden')) return;
    const [color, label] = inventoryState(data, variant);
    const key = `${input.value}:${color}:${label}`;
    if (inventory.dataset.lcStatusKey === key && inventory.textContent.trim() === label) return;
    const rgb = { green: '62,214,96', orange: '238,148,65', red: '255,0,0', grey: '200,200,200' }[color];
    // Fixed SVG markup only; merchant/customer data is assigned as text, never HTML.
    inventory.innerHTML = `<span class="svg-wrapper" style="color:rgb(${rgb})"><svg class="icon icon-inventory-status" width="15" height="15" viewBox="0 0 15 15" aria-hidden="true"><circle cx="7.5" cy="7.5" r="7.5" fill="currentColor" opacity=".3"/><circle cx="7.5" cy="7.5" r="5" stroke="white" stroke-width="1" fill="currentColor"/></svg></span>`;
    inventory.appendChild(document.createTextNode(` ${label}`));
    inventory.dataset.lcStatusKey = key;
  }

  function updatePurchaseLabels(root) {
    root.querySelectorAll('product-form').forEach((form) => {
      const button = form.querySelector('[type="submit"]');
      const label = button?.querySelector('span');
      if (!label || button.disabled || form.error || button.getAttribute('aria-disabled') === 'true') return;
      const map = parse(form.querySelector('[data-lc-preorder-map]'));
      const id = form.querySelector('input.product-variant-id')?.value;
      const text = map[String(id)] ? 'Pre-Order' : form.dataset.lcAddLabel || window.variantStrings?.addToCart;
      if (text && label.textContent.trim() !== text) label.textContent = text;
    });
  }

  function selectedOptions(root) {
    return [...root.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown')]
      .map((group) => group.querySelector('input:checked') || group.querySelector('select option:checked'))
      .filter(Boolean)
      .map((input) => ({ name: normalise(input.dataset.optionName), value: input.value }));
  }

  function updateColours(root) {
    const copy = window.LoveCashmereColourDescriptions;
    if (!copy) return;
    const options = selectedOptions(root);
    const selected = options.find((option) => ['color', 'colour'].includes(option.name));
    const value = selected?.value || root.querySelector('.swatch-input__input:checked')?.value;
    if (!value) return;
    [['[id="colour-description"]', 'standard'], ['[id="colour-description_blankets"]', 'blankets'], ['[id="colour-description_mens-knitwear"]', 'mensKnitwear']].forEach(([selector, type]) => {
      root.querySelectorAll(selector).forEach((element) => {
        const text = copy[type](value);
        if (element.textContent !== text) element.textContent = text;
      });
    });
  }

  function updateShareLinks(scope = document) {
    const url = encodeURIComponent(window.location.href);
    const links = {
      'facebook-share': `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      'pinterest-share': `https://www.pinterest.com/pin/create/button/?url=${url}`,
      'twitter-share': `https://twitter.com/intent/tweet?url=${url}&text=${encodeURIComponent('Check this out!')}`,
      'email-share': `mailto:?subject=${encodeURIComponent('Check this out')}&body=${encodeURIComponent(`Check out this page: ${window.location.href}`)}`,
    };
    Object.entries(links).forEach(([id, href]) => scope.querySelectorAll(`[id="${id}"]`).forEach((link) => { link.href = href; }));
  }

  function initialiseAccordions(scope) {
    findAll(scope, '.product__accordion details').forEach((details) => {
      if (accordionStates.has(details)) return;
      const summary = details.querySelector('summary');
      const content = details.querySelector('.accordion__content');
      if (!summary || !content) return;
      const state = { open: false };
      accordionStates.set(details, state);
      details.open = false;
      details.classList.remove('is-open');
      summary.setAttribute('aria-expanded', 'false');
      content.style.overflow = 'hidden';
      content.style.height = '0px';
      content.style.opacity = '0';
      content.style.padding = '0rem 2rem';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      content.style.transition = reducedMotion ? 'none' : 'height 0.8s ease, opacity 0.8s ease, padding 0.6s ease';
      const closeImmediately = () => {
        state.open = false;
        details.open = false;
        details.classList.remove('is-open');
        summary.setAttribute('aria-expanded', 'false');
        content.inert = true;
        content.style.height = '0px';
        content.style.opacity = '0';
      };
      details.addEventListener('keyup', (event) => {
        if ((event.code || event.key).toLowerCase() !== 'escape') return;
        closeImmediately();
        summary.focus();
      });
      details.addEventListener('toggle', () => {
        // Studio's Escape handler and other native closures must also reset animation state.
        if (!details.open) closeImmediately();
      });
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (!details.open) state.open = false;
        state.open = !state.open;
        summary.setAttribute('aria-expanded', String(state.open));
        details.classList.toggle('is-open', state.open);
        if (state.open) {
          details.open = true;
          content.inert = false;
          content.style.height = '0px';
          void content.offsetHeight;
          content.style.height = `${content.scrollHeight + 36}px`;
          content.style.opacity = '1';
          if (reducedMotion) content.style.height = 'auto';
        } else {
          content.inert = true;
          content.style.height = `${content.scrollHeight}px`;
          void content.offsetHeight;
          content.style.height = '0px';
          content.style.opacity = '0';
          if (reducedMotion) details.open = false;
        }
      });
      content.inert = true;
      content.addEventListener('transitionend', (event) => {
        if (event.propertyName !== 'height') return;
        if (state.open) content.style.height = 'auto';
        else details.open = false;
      });
    });
  }

  function fetchProduct(url) {
    const request = new URL(url, window.location.origin);
    request.search = '';
    request.hash = '';
    if (!request.pathname.endsWith('.js')) request.pathname = request.pathname.replace(/\/$/, '') + '.js';
    const key = request.toString();
    if (!productData.has(key)) {
      productData.set(key, fetch(key, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error('Unable to load product');
        return response.json();
      }).catch((error) => { productData.delete(key); throw error; }));
    }
    return productData.get(key);
  }

  function pickBestVariant(product, color, size) {
    const names = (product.options || []).map((option) => normalise(typeof option === 'string' ? option : option.name));
    const colorIndex = names.findIndex((name) => ['color', 'colour'].includes(name));
    const sizeIndex = names.indexOf('size');
    const variants = product.variants || [];
    const available = variants.filter((variant) => variant.available);
    const pool = available.length ? available : variants;
    const matchesColor = (variant) => color && colorIndex >= 0 && normalise(variant.options[colorIndex]) === normalise(color);
    const matchesSize = (variant) => size && sizeIndex >= 0 && normalise(variant.options[sizeIndex]) === normalise(size);
    return pool.find((variant) => matchesColor(variant) && matchesSize(variant)) || pool.find(matchesColor) || pool.find(matchesSize) || pool[0];
  }

  async function changeNeck(control) {
    if (control.classList.contains('is-active') || control.getAttribute('aria-disabled') === 'true' || control.dataset.lcLoading) return;
    const root = control.closest('product-info');
    const href = control.dataset.neckUrl;
    if (!root || !href) return;
    const options = selectedOptions(root);
    const color = options.find((option) => ['colour', 'color'].includes(option.name))?.value;
    const size = options.find((option) => option.name === 'size')?.value;
    control.dataset.lcLoading = 'true';
    try {
      const product = await fetchProduct(href);
      const variant = pickBestVariant(product, color, size);
      const url = new URL(href, window.location.origin);
      if (variant?.id) url.searchParams.set('variant', variant.id);
      window.location.assign(url.toString());
    } catch {
      // Preserve the working product link when the optional matching request fails.
      window.location.assign(href);
    } finally { delete control.dataset.lcLoading; }
  }

  async function updateMediaGroups(root) {
    const marker = root.querySelector('[data-lc-media-grouping]');
    const gallery = root.querySelector('media-gallery');
    if (!marker || !gallery || !root.dataset.url) return;
    const productUrl = root.dataset.url;
    try {
      const product = await fetchProduct(productUrl);
      if (!root.isConnected || root.dataset.url !== productUrl) return;
      const wanted = (marker.dataset.optionNames || 'Color,Colour').split(',').map(normalise);
      const options = product.options || [];
      const colorIndex = options.findIndex((option) => wanted.includes(normalise(typeof option === 'string' ? option : option.name)));
      if (colorIndex < 0) return;
      const selected = selectedOptions(root).find((option) => wanted.includes(option.name))?.value;
      if (!selected) return;
      const media = product.media || [];
      const starts = new Map();
      (product.variants || []).forEach((variant) => {
        const color = variant.options[colorIndex];
        const featured = variant.featured_media || variant.featured_image;
        if (!featured || starts.has(color)) return;
        // Media order can include videos, so match the media ID before an image-only position.
        let position = media.findIndex((item) => String(item.id) === String(featured.id)) + 1;
        if (!position) position = Number(featured.position);
        if (position > 0) starts.set(color, position);
      });
      const ranges = [...starts].sort((a, b) => a[1] - b[1]);
      if (!starts.has(selected)) return; // Do not hide the whole gallery for a colour with no assigned image.
      const groups = new Map();
      ranges.forEach(([color, start], index) => {
        const end = ranges[index + 1]?.[1] || media.length + 1;
        media.forEach((item, mediaIndex) => {
          if (mediaIndex + 1 >= start && mediaIndex + 1 < end) groups.set(String(item.id), color);
        });
      });
      const modal = root.querySelector('product-modal') || document.getElementById(`ProductModal-${root.dataset.section}`);
      const nodes = [...gallery.querySelectorAll('li[data-media-id], li[data-target]'), ...(modal?.querySelectorAll('[data-media-id]') || [])];
      nodes.forEach((node) => {
        const raw = node.dataset.mediaId || node.dataset.target;
        const id = raw.split('-').pop();
        const mediaItem = media.find((item) => String(item.id) === id);
        const alt = node.getAttribute('alt') || node.querySelector('img')?.alt || mediaItem?.alt || '';
        const group = alt.startsWith('ALL -') ? '__ALL__' : groups.get(id);
        const hidden = !!group && group !== selected && group !== '__ALL__';
        node.classList.toggle('hide-media', hidden);
      });
      const active = gallery.querySelector('li[data-media-id].is-active');
      if (!active || active.classList.contains('hide-media')) {
        const first = gallery.querySelector('li[data-media-id]:not(.hide-media)');
        if (first) gallery.setActiveMedia?.(first.dataset.mediaId, false);
      }
      gallery.querySelectorAll('slider-component').forEach((slider) => slider.initPages?.());
    } catch (error) {
      // Images remain visible if optional grouping data cannot be fetched.
      console.warn('Love Cashmere: media grouping unavailable', error);
    }
  }

  function syncCustomVariantContent(root, html, variant) {
    const source = [...html.querySelectorAll('product-info')].find((node) => node.dataset.section === root.dataset.section) || html.querySelector('product-info');
    if (!source) return;
    const sourceMap = source.querySelector('[data-lc-inventory-map]');
    const map = root.querySelector('[data-lc-inventory-map]');
    if (map && sourceMap) map.textContent = sourceMap.textContent;
    const prices = source.querySelectorAll('[data-lc-variant-price]');
    root.querySelectorAll('[data-lc-variant-price]').forEach((price, index) => {
      if (prices[index]) {
        price.innerHTML = prices[index].innerHTML;
        price.hidden = false;
      }
    });
    root.querySelectorAll('product-form').forEach((form, index) => {
      const fresh = source.querySelectorAll('product-form')[index];
      const map = form.querySelector('[data-lc-preorder-map]');
      const freshMap = fresh?.querySelector('[data-lc-preorder-map]');
      if (map && freshMap) map.textContent = freshMap.textContent;
      if (!form.closest('[data-lc-secondary-product-form]')) return;
      const input = form.querySelector('input.product-variant-id');
      if (input) input.value = variant?.id || '';
      const disabled = !variant || !variant.available || fresh?.querySelector('[type="submit"]')?.disabled;
      form.toggleSubmitButton?.(disabled, window.variantStrings?.soldOut);
    });
    root.querySelectorAll('[data-lc-secondary-product-form] input[name="quantity"]').forEach((quantity, index) => {
      const fresh = source.querySelectorAll('[data-lc-secondary-product-form] input[name="quantity"]')[index];
      if (!fresh) return;
      ['min', 'max', 'step', 'data-min', 'data-cart-quantity'].forEach((attribute) => {
        if (fresh.hasAttribute(attribute)) quantity.setAttribute(attribute, fresh.getAttribute(attribute));
        else quantity.removeAttribute(attribute);
      });
      quantity.closest('quantity-input')?.validateQtyRules?.();
    });
  }

  function refreshProduct(root) {
    updateInventory(root);
    updatePurchaseLabels(root);
    updateColours(root);
    initialiseAccordions(root);
    updateMediaGroups(root);
  }

  function init(scope = document) {
    findAll(scope, 'product-info').forEach(refreshProduct);
    initialiseAccordions(scope);
    updateShareLinks(scope);
    window.LoveCashmereReviews?.init(scope);
  }

  function queueRoot(root) {
    if (!root) return;
    pendingRoots.add(root);
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      pendingRoots.forEach((item) => { if (item.isConnected) init(item); });
      pendingRoots.clear();
    });
  }

  function boot() {
    init();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        document.querySelectorAll('product-info').forEach((root) => {
          if (root.dataset.section !== String(data.sectionId)) return;
          syncCustomVariantContent(root, data.html, data.variant);
          refreshProduct(root);
        });
        updateShareLinks();
      });
    }
    // Observe inserted product/editor content only; no per-form observers or polling timers.
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches('product-info, variant-selects, product-form, .product__accordion, .product-review-rotator, .review-rotator') || node.querySelector('product-info, variant-selects, product-form, .product__accordion, .product-review-rotator, .review-rotator')) {
          queueRoot(node.closest('product-info') || node);
        }
      }));
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('change', (event) => {
    if (event.target.closest('variant-selects, product-form')) queueRoot(event.target.closest('product-info'));
  });
  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-neck-picker] [data-neck-url]');
    if (!control) return;
    event.preventDefault();
    changeNeck(control);
  });
  document.addEventListener('keydown', (event) => {
    if (!['Enter', ' '].includes(event.key)) return;
    const control = event.target.closest('[data-neck-picker] [data-neck-url]');
    if (!control) return;
    event.preventDefault();
    changeNeck(control);
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('lc:colour-descriptions-ready', () => init());

  // Keep hover behavior attached to the actual Knitwear item across editor reloads and reordering.
  const desktop = window.matchMedia('(min-width: 990px)');
  document.addEventListener('pointerover', (event) => {
    const details = event.target.closest('details[data-lc-hover-menu]');
    if (!details || !desktop.matches || event.pointerType === 'touch') return;
    details.open = true;
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
  });
  document.addEventListener('pointerout', (event) => {
    const details = event.target.closest('details[data-lc-hover-menu]');
    if (!details || !desktop.matches || details.contains(event.relatedTarget) || details.matches(':focus-within')) return;
    details.open = false;
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  });
  desktop.addEventListener('change', () => {
    if (desktop.matches) return;
    document.querySelectorAll('details[data-lc-hover-menu]').forEach((details) => {
      details.open = false;
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  });

  window.LoveCashmere = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
